import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SharePreview from '../cards/share/SharePreview';
import { LoadingButton } from '@lf/ui-primitives';
import { getShare, importShare } from '@lf/api-client/lfApiShare';
import { clearPendingShare, writePendingShare } from '../lib/share/pendingShare';
import { useProfile } from '../contexts/ProfileContext';

const ShareLandingPage = ({ fetchStudyPlans }) => {
    const { profile } = useProfile();
    const { token } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [share, setShare] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importError, setImportError] = useState(null);

    useEffect(() => {
        if (!token) return;
        writePendingShare(token);

        let cancelled = false;
        const load = async () => {
            const data = await getShare(token);
            if (cancelled) return;
            if (!data) {
                setNotFound(true);
                setLoading(false);
                return;
            }
            setShare(data);
            setLoading(false);
        };
        load();
        return () => { cancelled = true; };
    }, [token]);

    // Signed-out users get bounced to the LoginPage with the token in
    // the URL — the only two valid landing states are "signed-in and
    // ready to import" or "signed-out at the login form".
    useEffect(() => {
        if (!loading && !profile && share && !share.expired) {
            navigate(`/?share=${encodeURIComponent(token)}`, { replace: true });
        }
    }, [loading, profile, share, token, navigate]);

    const handleImport = async () => {
        if (!token || !profile || !profile.id) return;
        setImportError(null);
        setIsImporting(true);
        const result = await importShare(token, [profile.id], setImportError);
        setIsImporting(false);
        if (!result || !result.imported || result.imported.length === 0) {
            if (!importError) {
                setImportError({ message: 'Could not import. Please try again.' });
            }
            return;
        }

        clearPendingShare();
        // Refresh the App-level study-plans list so the global Header
        // includes the just-imported plan (and can render it selected
        // when the user lands on /feed?sp=<newId>) without a refresh.
        if (fetchStudyPlans) {
            try {
                await fetchStudyPlans();
            } catch (_) { /* navigation should not block on refresh */ }
        }
        const newId = result.imported[0].new_lesson_plan_id;
        if (newId) {
            navigate(`/studyPlan/${newId}`);
        } else {
            navigate('/home');
        }
    };

    if (loading) {
        return (
            <div className="max-w-2xl mx-auto p-4 text-center text-gray-600">
                Loading shared study plan...
            </div>
        );
    }

    if (notFound || !share) {
        return (
            <div className="max-w-2xl mx-auto p-4">
                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                    <h1 className="text-xl font-semibold mb-2">Share link not found</h1>
                    <p className="text-gray-600">
                        We couldn't find a shared study plan at this URL.
                    </p>
                </div>
            </div>
        );
    }

    if (share.expired) {
        return (
            <div className="max-w-2xl mx-auto p-4">
                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                    <h1 className="text-xl font-semibold mb-2">
                        This share link is no longer active
                    </h1>
                    <p className="text-gray-600 mb-4">
                        Ask the person who shared it for a fresh link, or sign
                        up to create your own study plans.
                    </p>
                    <button
                        type="button"
                        className="bg-blue-500 text-white py-2 px-4 rounded"
                        onClick={() => navigate('/signup')}>
                        Sign up
                    </button>
                </div>
            </div>
        );
    }

    if (!profile) {
        // Brief loading state while the redirect-to-login effect fires.
        return (
            <div className="max-w-2xl mx-auto p-4 text-center text-gray-600">
                Redirecting...
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto p-4">
            <div className="flex justify-end mb-4">
                <LoadingButton
                    text="Import study plan"
                    isLoading={isImporting}
                    onClick={handleImport} />
            </div>

            <SharePreview
                snapshot={share.snapshot}
                snapshotTitle={share.snapshot_title}
                sharerDisplayName={share.sharer_display_name}
                createdAt={share.created_at} />

            {importError && (
                <div className="text-red-500 text-sm mb-4 text-right">
                    {importError.message || 'Could not import. Please try again.'}
                </div>
            )}
        </div>
    );
};

export default ShareLandingPage;
